import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATH_OPTIONS = [
  "Co-Own (I want to use the apartment)",
  "Co-Invest (purely investment)",
  "Own outright (100% sole ownership)",
  "Open to any of these",
];
const LOCATIONS = ["Tel Aviv", "Jerusalem", "Herzliya", "Netanya", "Other / Open"];
const OWNERSHIP = ["25%", "33%", "50%", "100% (sole ownership)"];
const BUDGETS = [
  "₪750k – ₪1M",
  "₪1M – ₪1.5M",
  "₪1.5M – ₪2M",
  "₪2M – ₪3M",
  "₪3M – ₪5M",
  "₪5M+",
];
const MONTHS = ["1 month", "2 months", "3 months", "4+ months", "All year", "N/A — investment only"];
const TIMELINES = ["Immediately", "3 – 6 months", "Just exploring"];

const initialState = {
  full_name: "",
  email: "",
  phone: "",
  country: "",
  path_of_interest: "",
  preferred_location: "",
  ownership_preference: "",
  budget_range: "",
  months_per_year: "",
  timeline: "",
  best_time_for_call: "",
  notes: "",
};

const initialReferral = {
  referee_name: "",
  referee_email: "",
  referee_phone: "",
  relationship: "",
  message: "",
};

export default function LeadForm() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [referral, setReferral] = useState(initialReferral);
  const [referralSubmitting, setReferralSubmitting] = useState(false);
  const [referralCount, setReferralCount] = useState(0);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v?.target?.value ?? "" }));
  const setRef = (k) => (v) => setReferral((f) => ({ ...f, [k]: typeof v === "string" ? v : v?.target?.value ?? "" }));

  const REQUIRED = [
    "full_name", "email", "phone", "country", "path_of_interest",
    "preferred_location", "ownership_preference", "budget_range",
    "months_per_year", "timeline",
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    for (const key of REQUIRED) {
      if (!form[key] || !form[key].trim()) {
        toast.error("Please complete all required fields.");
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form, { timeout: 20000 });
      setSubmitted(true);
      toast.success("Thank you. We’ll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitReferral = async (e) => {
    e.preventDefault();
    if (!referral.referee_name || !referral.referee_name.trim()) {
      toast.error("Please share the family’s name.");
      return;
    }
    setReferralSubmitting(true);
    try {
      await axios.post(
        `${API}/referrals`,
        {
          referrer_name: form.full_name,
          referrer_email: form.email,
          referee_name: referral.referee_name,
          referee_email: referral.referee_email || null,
          referee_phone: referral.referee_phone,
          relationship: referral.relationship,
          message: referral.message,
        },
        { timeout: 20000 }
      );
      setReferralCount((c) => c + 1);
      setReferral(initialReferral);
      toast.success("Thank you. We’ll reach out with discretion.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send the referral. Please try again.";
      toast.error(msg);
    } finally {
      setReferralSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="register" data-testid="lead-capture-section" className="relative bg-[var(--bone)] text-[var(--ink)]">
        <div className="container mx-auto px-6 md:px-12 lg:px-16 max-w-[1400px] py-24 md:py-36">
          <div
            data-testid="lead-form-success"
            className="max-w-2xl mx-auto border border-[var(--border-soft)] p-10 md:p-14 bg-[var(--bone-soft)]"
          >
            <div className="text-center">
              <CheckCircle2 className="h-10 w-10 text-[var(--gold)] mx-auto" strokeWidth={1.5} />
              <h2 className="mt-6 font-serif-display text-3xl md:text-5xl tracking-tight">
                Thank you.
              </h2>
              <p className="mt-4 text-[var(--ink)]/70 font-light text-base md:text-lg leading-relaxed">
                We’ve received your details. A member of our team will reach out within
                two business days to arrange a confidential, face‑to‑face video call.
              </p>
            </div>

            {/* Referral mini-form */}
            <div className="mt-10 pt-8 border-t border-[var(--border-soft)]">
              <div className="text-overline text-[var(--gold)] mb-3">One more thing</div>
              <h3 className="font-serif-display text-2xl md:text-[2rem] tracking-tight leading-tight">
                Know another family who’d be a good fit?
              </h3>
              <p className="mt-3 text-[var(--ink)]/65 text-sm md:text-base font-light leading-relaxed">
                Our best partnerships come from quiet introductions. Share a name in confidence and
                we’ll reach out with discretion — they’ll never know it came from you unless you’d like them to.
              </p>

              {referralCount > 0 && (
                <div
                  data-testid="referral-thanks"
                  className="mt-5 px-4 py-3 bg-white border border-[var(--border-soft)] text-sm text-[var(--ink)]/75 flex items-start gap-3"
                >
                  <CheckCircle2 className="h-4 w-4 text-[var(--gold)] mt-0.5 shrink-0" strokeWidth={2} />
                  <span>
                    {referralCount === 1
                      ? "Referral received — thank you. Feel free to add another."
                      : `${referralCount} referrals received — thank you. Add another below.`}
                  </span>
                </div>
              )}

              <form
                data-testid="referral-form"
                onSubmit={onSubmitReferral}
                className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <div className="flex flex-col gap-2">
                  <Label className="text-overline text-[var(--ink)]/60">
                    Their family / name <span className="text-[var(--terracotta)]">*</span>
                  </Label>
                  <Input
                    data-testid="referral-input-name"
                    value={referral.referee_name}
                    onChange={setRef("referee_name")}
                    placeholder="e.g. The Levy family"
                    className="h-11 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-overline text-[var(--ink)]/60">Their email (optional)</Label>
                  <Input
                    data-testid="referral-input-email"
                    type="email"
                    value={referral.referee_email}
                    onChange={setRef("referee_email")}
                    placeholder="levy@example.com"
                    className="h-11 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-overline text-[var(--ink)]/60">Their phone (optional)</Label>
                  <Input
                    data-testid="referral-input-phone"
                    value={referral.referee_phone}
                    onChange={setRef("referee_phone")}
                    placeholder="+61 4xx xxx xxx"
                    className="h-11 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-overline text-[var(--ink)]/60">How do you know them?</Label>
                  <Input
                    data-testid="referral-input-relationship"
                    value={referral.relationship}
                    onChange={setRef("relationship")}
                    placeholder="Cousins · shul friends · etc."
                    className="h-11 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                  />
                </div>
                <div className="flex flex-col gap-2 md:col-span-2">
                  <Label className="text-overline text-[var(--ink)]/60">A short note (optional)</Label>
                  <Textarea
                    data-testid="referral-input-message"
                    value={referral.message}
                    onChange={setRef("message")}
                    placeholder="Anything we should know — preferred city, family stage, when to reach out, whether to mention you…"
                    rows={3}
                    className="rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                  />
                </div>

                <div className="md:col-span-2 mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-[var(--ink)]/55 max-w-md leading-relaxed">
                    Held in strict confidence. We’ll reach out personally — and only if it’s the right fit.
                  </p>
                  <Button
                    data-testid="referral-submit-btn"
                    type="submit"
                    disabled={referralSubmitting}
                    className="rounded-full h-11 md:h-12 px-6 md:px-8 bg-[var(--ink)] text-[var(--bone)] hover:bg-[var(--ink-hover)] text-sm font-medium tracking-wide transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
                  >
                    {referralSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      "Share this introduction"
                    )}
                  </Button>
                </div>
              </form>
            </div>

            <div className="mt-10 pt-6 border-t border-[var(--border-soft)] text-center">
              <Button
                data-testid="lead-form-reset-btn"
                variant="ghost"
                className="rounded-full border border-[var(--border-soft)]"
                onClick={() => {
                  setForm(initialState);
                  setReferral(initialReferral);
                  setReferralCount(0);
                  setSubmitted(false);
                }}
              >
                Submit another enquiry
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="register" data-testid="lead-capture-section" className="relative bg-[var(--bone)] text-[var(--ink)]">
      <div className="container mx-auto px-6 md:px-12 lg:px-16 max-w-[1400px] py-24 md:py-36 lg:py-44">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 self-start reveal">
            <div className="text-overline text-[var(--ink)]/55 mb-6">Register your interest</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight">
              A quiet, confidential <span className="italic text-[var(--gold)]">conversation.</span>
            </h2>
            <p className="mt-7 text-[var(--ink)]/70 font-light text-base md:text-lg leading-relaxed max-w-md">
              Share a few details and we&rsquo;ll arrange a face‑to‑face video call to understand your
              needs, walk you through the three paths — Co‑Own, Co‑Invest or full ownership — and
              explore what suits you best.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-y-5 gap-x-8 max-w-md">
              <div>
                <div className="text-overline text-[var(--ink)]/45">Step 1</div>
                <div className="mt-1 text-[var(--ink)]/80 font-light text-sm">You share</div>
              </div>
              <div>
                <div className="text-overline text-[var(--ink)]/45">Step 2</div>
                <div className="mt-1 text-[var(--ink)]/80 font-light text-sm">We listen</div>
              </div>
              <div>
                <div className="text-overline text-[var(--ink)]/45">Step 3</div>
                <div className="mt-1 text-[var(--ink)]/80 font-light text-sm">We match</div>
              </div>
              <div>
                <div className="text-overline text-[var(--ink)]/45">Step 4</div>
                <div className="mt-1 text-[var(--ink)]/80 font-light text-sm">You arrive home</div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <form
            data-testid="lead-form"
            onSubmit={onSubmit}
            className="lg:col-span-7 bg-[var(--bone-soft)] border border-[var(--border-soft)] p-7 md:p-12 reveal"
          >
            {/* Path of interest — primary qualifier */}
            <div className="mb-8 md:mb-10">
              <Label className="text-overline text-[var(--ink)]/60">
                Which path interests you? <span className="text-[var(--terracotta)]">*</span>
              </Label>
              <div
                role="radiogroup"
                aria-label="Path of interest"
                className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-soft)] border border-[var(--border-soft)]"
              >
                {[
                  { value: PATH_OPTIONS[0], headline: "Co-Own", caption: "I want to use the apartment" },
                  { value: PATH_OPTIONS[1], headline: "Co-Invest", caption: "Purely investment" },
                  { value: PATH_OPTIONS[2], headline: "Own outright", caption: "I want to own 100%" },
                  { value: PATH_OPTIONS[3], headline: "Open to any", caption: "Show me everything" },
                ].map((opt) => {
                  const selected = form.path_of_interest === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      role="radio"
                      aria-checked={selected}
                      data-testid={`lead-path-${opt.headline.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => set("path_of_interest")(opt.value)}
                      className={`text-left p-5 md:p-6 transition-colors duration-200 ${
                        selected
                          ? "bg-[var(--ink)] text-[var(--bone)]"
                          : "bg-white text-[var(--ink)] hover:bg-[var(--bone)]"
                      }`}
                    >
                      <div
                        className="font-serif-display text-xl md:text-2xl leading-tight tracking-tight"
                        style={selected ? { color: "#E87A4C" } : {}}
                      >
                        {opt.headline}
                      </div>
                      <div
                        className={`mt-1 text-xs md:text-[0.82rem] font-light leading-snug ${
                          selected ? "text-[var(--bone)]/75" : "text-[var(--ink)]/60"
                        }`}
                      >
                        {opt.caption}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              <Field label="Full name" required>
                <Input
                  data-testid="lead-input-full-name"
                  value={form.full_name}
                  onChange={set("full_name")}
                  placeholder="Daniel & Sarah Cohen"
                  className="h-12 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>
              <Field label="Email" required>
                <Input
                  data-testid="lead-input-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="daniel@example.com"
                  className="h-12 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>
              <Field label="Phone (with country code)" required>
                <Input
                  data-testid="lead-input-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+61 4xx xxx xxx"
                  className="h-12 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>
              <Field label="Country of residence" required>
                <Input
                  data-testid="lead-input-country"
                  value={form.country}
                  onChange={set("country")}
                  placeholder="Australia"
                  className="h-12 rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>

              <SelectField
                label="Preferred location in Israel" required
                testid="lead-select-location"
                value={form.preferred_location}
                onChange={set("preferred_location")}
                options={LOCATIONS}
                placeholder="Choose a city"
              />
              <SelectField
                label="Ownership preference" required
                testid="lead-select-ownership"
                value={form.ownership_preference}
                onChange={set("ownership_preference")}
                options={OWNERSHIP}
                placeholder="25% · 33% · 50%"
              />
              <SelectField
                label="Budget range" required
                testid="lead-select-budget"
                value={form.budget_range}
                onChange={set("budget_range")}
                options={BUDGETS}
                placeholder="Select your range"
              />
              <SelectField
                label="Months in Israel per year" required
                testid="lead-select-months"
                value={form.months_per_year}
                onChange={set("months_per_year")}
                options={MONTHS}
                placeholder="How long would you stay?"
              />
              <SelectField
                label="When would you move forward" required
                testid="lead-select-timeline"
                value={form.timeline}
                onChange={set("timeline")}
                options={TIMELINES}
                placeholder="Your timeline"
              />

              <Field label="Best time for a video call" full>
                <Textarea
                  data-testid="lead-input-call-time"
                  value={form.best_time_for_call}
                  onChange={set("best_time_for_call")}
                  placeholder="Preferred days, evenings, time zone (e.g. weekdays 7–9pm AEDT)"
                  rows={2}
                  className="rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>

              <Field label="Anything else we should know?" full>
                <Textarea
                  data-testid="lead-input-notes"
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Family profile, religious considerations, preferred months in Israel, concerns…"
                  rows={4}
                  className="rounded-none bg-white border-[var(--border-soft)] focus-visible:ring-1 focus-visible:ring-[var(--ink)]"
                />
              </Field>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <p className="text-xs text-[var(--ink)]/55 max-w-md leading-relaxed">
                Your details are kept strictly confidential. We’ll only use them to arrange a private
                conversation and explore suitable matches.{" "}
                <a
                  href="/privacy"
                  data-testid="lead-form-privacy-link"
                  className="underline decoration-[var(--ink)]/40 underline-offset-2 hover:text-[var(--ink)] hover:decoration-[var(--ink)] transition-colors"
                >
                  Read our privacy notice
                </a>
                .
              </p>
              <Button
                data-testid="lead-submit-btn"
                type="submit"
                disabled={submitting}
                className="rounded-full h-12 md:h-14 px-7 md:px-10 bg-[var(--ink)] text-[var(--bone)] hover:bg-[var(--ink-hover)] text-sm md:text-base font-medium tracking-wide transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Request a confidential call"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, required, full }) {
  return (
    <div className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-overline text-[var(--ink)]/60">
        {label} {required && <span className="text-[var(--terracotta)]">*</span>}
      </Label>
      {children}
    </div>
  );
}

function SelectField({ label, required, testid, value, onChange, options, placeholder, full }) {
  return (
    <Field label={label} required={required} full={full}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          data-testid={testid}
          className="h-12 rounded-none bg-white border-[var(--border-soft)] focus:ring-1 focus:ring-[var(--ink)]"
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="rounded-none">
          {options.map((o) => (
            <SelectItem key={o} value={o} data-testid={`${testid}-option-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}
